import { Component, type ReactNode } from "react";

/**
 * Error boundary alrededor del Suspense de rutas.
 *
 * Problema que resuelve: los routes son `lazy(() => import(...))`. Si un
 * `import()` REJECTA — típicamente cuando el tab tiene el index.html viejo
 * cacheado y, tras un deploy, pide un chunk con un hash que ya no existe (404),
 * o por un fallo de red transitorio — Suspense NO atrapa errores, el error se
 * propaga sin boundary y React desmonta todo el árbol → PANTALLA BLANCA.
 *
 * Síntoma reportado: "al ir atrás / a Pacientes desde el nav, a veces queda en
 * blanco". "A veces" = solo cuando un chunk falla (post-deploy / red).
 *
 * Fix:
 *   - error de chunk → recargar la página (trae index.html fresco + hashes
 *     correctos), con guard temporal para no entrar en loop de reloads.
 *   - cualquier otro error de render → fallback con botón Recargar (no blanco).
 *
 * Corrección (2026-09-07): `window.location.reload()` no garantiza bypass
 * de caché — con `index.html` servido `must-revalidate`, un GET condicional
 * (If-None-Match) puede recibir 304 de un edge de Vercel que todavía no
 * propagó el último deploy (lag de CDN post `vercel --prod`), devolviendo
 * el `index.html` VIEJO de nuevo y dejando al usuario en este mismo cartel
 * en vez de resolverse solo. `hardReload()` fuerza una URL nueva (query
 * param con timestamp) vía `location.replace` — un GET normal, no
 * condicional, que un edge desactualizado sirve igual pero que evita el
 * camino de re-uso de caché del navegador. No es 100% infalible contra un
 * edge de Vercel realmente desactualizado (eso se resuelve solo en
 * segundos), pero elimina la causa más común de que el auto-reload o el
 * botón "Recargar" no alcancen.
 *
 * Estilos inline a propósito: debe renderizar aunque el CSS/algo esté roto.
 */
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk|ChunkLoadError|dynamically imported module/i;
const RELOAD_KEY = "ecgwatch.chunk_reload_at";
const RELOAD_WINDOW_MS = 10_000;
const RELOAD_PARAM = "_r";

function isChunkError(error: unknown): boolean {
  const msg =
    error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return CHUNK_ERROR_RE.test(msg);
}

/**
 * Recarga forzando una URL "nueva" (query param con timestamp) para evitar
 * que el navegador reutilice una respuesta cacheada/condicional de
 * `index.html`. `location.replace` (no `href=`) para no ensuciar el
 * historial con cada intento de recuperación.
 */
function hardReload(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(RELOAD_PARAM, String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    // URL/searchParams no debería fallar en un browser real, pero por las
    // dudas: fallback al reload simple antes que no hacer nada.
    window.location.reload();
  }
}

interface State {
  error: Error | null;
}

export class RouteErrorBoundary extends Component<
  { children: ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error): void {
    if (!isChunkError(error)) return;
    // Recargar UNA vez para recuperar de un chunk viejo post-deploy. Guard:
    // no recargar de nuevo dentro de RELOAD_WINDOW_MS (evita loop si el chunk
    // genuinamente no existe); pasado ese window, futuros deploys sí recargan.
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
    } catch {
      /* sessionStorage puede fallar en modo privado */
    }
    if (Date.now() - last > RELOAD_WINDOW_MS) {
      try {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      } catch {
        /* noop */
      }
      hardReload();
    }
  }

  render(): ReactNode {
    if (this.state.error) {
      const chunk = isChunkError(this.state.error);
      return (
        <div
          style={{
            minHeight: "100vh",
            background: "#0A0E14",
            color: "#F0F2F5",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            fontFamily: "system-ui, -apple-system, sans-serif",
            padding: 24,
            textAlign: "center",
          }}
        >
          <p style={{ fontSize: 16, color: "#8A95A8", maxWidth: 360, margin: 0 }}>
            {chunk
              ? "Actualizando la aplicación…"
              : "Algo salió mal al cargar esta vista."}
          </p>
          <button
            type="button"
            onClick={() => {
              try {
                sessionStorage.removeItem(RELOAD_KEY);
              } catch {
                /* noop */
              }
              hardReload();
            }}
            style={{
              background: "#00A3E0",
              color: "#0A0E14",
              border: "none",
              borderRadius: 8,
              padding: "10px 20px",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Recargar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
